/**
 * Affluence Paradox Appendix Slide
 * "Problems Money Can't Solve"
 * Concerted cultivation, activity escalation, scarcity vs optionality,
 * management overhead of outsourcing
 * Sources: Lareau 2003/2011, Pew 2015/2024, Ciciolla & Luthar 2019,
 *          Haupt & Gelbgiser 2024
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing } from '@config/spacing';
import {
  AppendixSlideLayout,
  AppendixContentArea,
  SlideHeader,
  AppendixColumn,
  AppendixSectionLabel,
  AppendixStatBar,
  AppendixStatNumber,
  AppendixStatDescription,
  AppendixStatSource,
  AppendixDataTable,
  AppendixDataRow,
  AppendixDataHeader,
  AppendixCellHighlight,
  DarkInsightBox,
  DarkInsightTitle,
  DarkInsightBody,
  DarkInsightEmphasis,
  AppendixSourceFooter,
  AccentBorderedCard,
} from '@components/press/pitch-deck/slide-primitives';

// ============================================
// Local overrides & components
// ============================================

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const CompactAccentCard = styled(AccentBorderedCard)`
  padding: ${spacing.xs} ${spacing.md};
`;

const CardTitle = styled.span`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 0.85vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
`;

const CardBody = styled.span`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.sm});
  color: ${colors.highlight.primary};
  line-height: 1.4;
`;

// ============================================
// Component
// ============================================

export const AffluenceParadoxSlide: React.FC = () => (
  <AppendixSlideLayout>
    <SlideHeader
      title="Problems Money Can&#x2019;t Solve"
      subtitle={<>Higher income creates more complexity, not less — affluent families face <em>more</em> cognitive load, not less</>}
    />

    <AppendixContentArea>
      <AppendixColumn>
        <AppendixSectionLabel>The Concerted Cultivation Escalator</AppendixSectionLabel>

        <AppendixStatBar $bg={`${colors.accent.orange.extraLight}50`} $accent={colors.accent.orange.exxtraDark}>
          <AppendixStatNumber $color={colors.accent.orange.exxtraDark}>4.9</AppendixStatNumber>
          <AppendixStatDescription>
            concurrent organized activities per child in upper-middle-class families.
            Each activity adds scheduling, communication, logistics, and financial tracking.{' '}
            <AppendixStatSource>(Lareau, Unequal Childhoods, 2003/2011)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar $bg={`${colors.accent.orange.extraLight}50`} $accent={colors.accent.orange.exxtraDark}>
          <AppendixStatNumber $color={colors.accent.orange.exxtraDark}>84%</AppendixStatNumber>
          <AppendixStatDescription>
            of families earning $75K+ have children in organized sports &mdash;
            vs. 59% of families under $30K. Higher income = more activities = more logistics.{' '}
            <AppendixStatSource>(Pew Research, 2015)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar $bg={`${colors.accent.orange.extraLight}50`} $accent={colors.accent.orange.exxtraDark}>
          <AppendixStatNumber $color={colors.accent.orange.exxtraDark}>50%</AppendixStatNumber>
          <AppendixStatDescription>
            of high-income parents report that managing their phone and digital life
            is a significant source of stress. More resources = more services = more apps
            to manage.{' '}
            <AppendixStatSource>(Pew Research, 2024)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixSectionLabel>Scarcity vs. Optionality</AppendixSectionLabel>

        <AppendixDataTable>
          <AppendixDataHeader>
            <div>Dimension</div>
            <div>Lower Income</div>
            <div>Higher Income</div>
          </AppendixDataHeader>
          <AppendixDataRow>
            <div>Primary constraint</div>
            <div>Money (fewer options)</div>
            <AppendixCellHighlight $color={colors.accent.orange.exxtraDark}>Time &amp; decisions (too many options)</AppendixCellHighlight>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Kids&rsquo; activities</div>
            <div>1&ndash;2, neighborhood-based</div>
            <AppendixCellHighlight $color={colors.accent.orange.exxtraDark}>4&ndash;6, across town, competitive</AppendixCellHighlight>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Service providers</div>
            <div>Few, informal network</div>
            <AppendixCellHighlight $color={colors.accent.orange.exxtraDark}>10+: nanny, tutor, coach, therapist&hellip;</AppendixCellHighlight>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Decision complexity</div>
            <div>&ldquo;Can we afford it?&rdquo;</div>
            <AppendixCellHighlight $color={colors.accent.orange.exxtraDark}>&ldquo;Which of 15 options is optimal?&rdquo;</AppendixCellHighlight>
          </AppendixDataRow>
          <AppendixDataRow>
            <div>Type of stress</div>
            <div>Material deprivation</div>
            <AppendixCellHighlight $color={colors.accent.orange.exxtraDark}>Cognitive overload &amp; optimization anxiety</AppendixCellHighlight>
          </AppendixDataRow>
        </AppendixDataTable>
      </AppendixColumn>

      <AppendixColumn>
        <AppendixSectionLabel>What Money Can&rsquo;t Buy</AppendixSectionLabel>

        <CardList>
          <CompactAccentCard $accent={colors.accent.coral.exxtraDark}>
            <CardTitle>Coordination across providers: </CardTitle>
            <CardBody>
              Nanny, tutor, therapist, coach, babysitter &mdash; each needs scheduling,
              communication, and payment. Mom becomes the project manager.
            </CardBody>
          </CompactAccentCard>

          <CompactAccentCard $accent={colors.accent.blue.exxtraDark}>
            <CardTitle>Decision quality at scale: </CardTitle>
            <CardBody>
              More money means more choices &mdash; which preschool, which camp, which
              enrichment program. Research, compare, decide, repeat &times; N children.
            </CardBody>
          </CompactAccentCard>

          <CompactAccentCard $accent={colors.accent.purple.exxtraDark}>
            <CardTitle>Emotional labor of privilege: </CardTitle>
            <CardBody>
              Managing household staff, navigating social expectations, maintaining the
              &ldquo;effortless&rdquo; family image. Outsourcing tasks increases relational complexity.{' '}
              <AppendixStatSource>(Ciciolla &amp; Luthar, 2019)</AppendixStatSource>
            </CardBody>
          </CompactAccentCard>

          <CompactAccentCard $accent={colors.accent.teal.exxtraDark}>
            <CardTitle>Integration across domains: </CardTitle>
            <CardBody>
              No amount of spending connects the school calendar to the meal plan to the
              budget to the activity schedule. Each outsourced service is another silo.
            </CardBody>
          </CompactAccentCard>

          <CompactAccentCard $accent={colors.accent.yellow.exxtraDark}>
            <CardTitle>The oversight tax: </CardTitle>
            <CardBody>
              Hired help requires management: vetting, training, quality control, backup plans.
              Delegation without systems creates <em>more</em> cognitive load, not less.{' '}
              <AppendixStatSource>(Haupt &amp; Gelbgiser, 2024)</AppendixStatSource>
            </CardBody>
          </CompactAccentCard>
        </CardList>

        <DarkInsightBox>
          <DarkInsightTitle>The Outsourcing Paradox</DarkInsightTitle>
          <DarkInsightBody>
            <DarkInsightEmphasis>Every outsourced service creates a new management task.</DarkInsightEmphasis>{' '}
            Hire a cleaner &rarr; manage the cleaner&rsquo;s schedule, supplies, and access.
            Hire a tutor &rarr; coordinate with school, track progress, handle billing.
            The cognitive load doesn&rsquo;t decrease &mdash; it transforms from
            &ldquo;doing&rdquo; into &ldquo;managing,&rdquo; which is harder to see,
            harder to share, and harder to escape.
          </DarkInsightBody>
        </DarkInsightBox>
      </AppendixColumn>
    </AppendixContentArea>

    <AppendixSourceFooter>
      Lareau, <em>Unequal Childhoods</em> (2003, 2011) &middot;
      Pew Research Center (2015, 2024) &middot;
      Ciciolla &amp; Luthar (2019) &middot;
      Haupt &amp; Gelbgiser (2024)
    </AppendixSourceFooter>
  </AppendixSlideLayout>
);
